import { useState, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import { extname } from 'path-browserify';
import { supabase } from '../lib/supabase';
import { mediaService } from '../lib/supabase-service';

// Media type configurations for Supabase buckets
export const MEDIA_TYPES = {
  AVATAR: 'mozo_avatars',
  COVER: 'mozo_avatars',
  POST_IMAGE: 'mozo_posts',
  POST_VIDEO: 'mozo_posts',
  POST_AUDIO: 'mozo_posts',
  STORY: 'mozo_stories',
  STORY_VIDEO: 'mozo_stories',
  MESSAGE_MEDIA: 'mozo_media',
  MESSAGE_VIDEO: 'mozo_media',
} as const;

export type MediaType = (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES];

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadResult {
  success: boolean;
  url?: string;
  key?: string;
  error?: string | null;
  filePath?: string | null;
}

/**
 * Helper function to determine the correct media type based on file type and context
 */
export const getMediaTypeForFile = (
  _file: File,
  context: 'avatar' | 'post' | 'story' | 'message' | 'cover'
): MediaType => {
  switch (context) {
    case 'avatar':
      return MEDIA_TYPES.AVATAR;
    case 'cover':
      return MEDIA_TYPES.COVER;
    case 'post':
      return MEDIA_TYPES.POST_IMAGE; // Use posts bucket for all post media
    case 'story':
      return MEDIA_TYPES.STORY;
    case 'message':
      return MEDIA_TYPES.MESSAGE_MEDIA;
    default:
      return MEDIA_TYPES.POST_IMAGE;
  }
};

export interface UseMediaUploadOptions {
  onSuccess?: (result: UploadResult) => void;
  onError?: (error: string) => void;
  onProgress?: (progress: UploadProgress) => void;
}

export interface UseMediaUploadReturn {
  upload: (file: File, mediaType: MediaType, userId?: string) => Promise<UploadResult>;
  uploadMultiple: (files: File[], mediaType: MediaType, userId?: string) => Promise<UploadResult[]>;
  deleteFile: (key: string) => Promise<{ success: boolean; error?: string }>;
  isUploading: boolean;
  progress: UploadProgress | null;
  error: string | null;
  getMediaTypeForFile: (file: File, context: 'avatar' | 'post' | 'story' | 'message' | 'cover') => MediaType;
}

/**
 * Custom hook for handling media uploads to Supabase Storage
 */
export const useMediaUpload = (options: UseMediaUploadOptions = {}): UseMediaUploadReturn => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useCallback(
    async (file: File, mediaType: MediaType, _userId?: string): Promise<UploadResult> => {
      setIsUploading(true);
      setError(null);
      setProgress(null);

      try {
        // Use Supabase storage service
        const result = await mediaService.uploadFile(file, mediaType);

        const uploadResult: UploadResult = {
          success: true,
          url: result.url,
          key: result.fileId,
        };

        options.onSuccess?.(uploadResult);
        return uploadResult;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        setError(errorMessage);

        const uploadResult: UploadResult = {
          success: false,
          error: errorMessage,
        };

        options.onError?.(errorMessage);
        return uploadResult;
      } finally {
        setIsUploading(false);
        setProgress(null);
      }
    },
    [options]
  );

  const uploadMultiple = useCallback(
    async (files: File[], mediaType: MediaType, userId?: string): Promise<UploadResult[]> => {
      console.log('🚀 useMediaUpload.uploadMultiple - Starting upload:', {
        filesCount: files.length,
        mediaType,
        userId,
        files: files.map(f => ({
          name: f.name,
          size: f.size,
          type: f.type,
          lastModified: f.lastModified,
          isFile: f instanceof File,
        })),
      });

      if (!files.length) {
        console.error('❌ useMediaUpload.uploadMultiple - No files provided');
        return [];
      }

      setIsUploading(true);
      setProgress(null);
      const results: UploadResult[] = [];

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('User not authenticated');
        }

        console.log('👤 useMediaUpload.uploadMultiple - User authenticated:', {
          id: user.id,
          email: user.email,
        });

        // Check if user profile exists, create if it doesn't
        const { data: profile } = await supabase.from('users').select('id').eq('id', user.id).single();

        if (!profile) {
          console.log('📝 useMediaUpload.uploadMultiple - Creating user profile');
          await supabase.from('users').insert([
            {
              id: user.id,
              username: user.email?.split('@')[0],
              email: user.email,
            },
          ]);
        }

        console.log('🗄️ useMediaUpload.uploadMultiple - Starting file upload to bucket:', mediaType);

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const targetUserId = userId || user.id;
          const filePath = `${targetUserId}/${uuid()}${extname(file.name)}`;

          console.log(`📤 useMediaUpload.uploadMultiple - Uploading file ${i + 1}/${files.length}:`, {
            filePath,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
          });

          const { error: uploadError } = await supabase.storage.from(mediaType).upload(filePath, file);

          if (uploadError) {
            console.error(`❌ useMediaUpload.uploadMultiple - Upload error for file ${i + 1}:`, uploadError);
            results.push({
              success: false,
              error: uploadError.message,
              filePath: null,
            });
            continue;
          }

          console.log(`✅ useMediaUpload.uploadMultiple - Successfully uploaded file ${i + 1}`);

          const { data: { publicUrl } } = supabase.storage.from(mediaType).getPublicUrl(filePath);

          results.push({
            success: true,
            error: null,
            filePath: publicUrl,
          });

          setProgress({
            loaded: i + 1,
            total: files.length,
            percentage: Math.round(((i + 1) / files.length) * 100),
          });
        }

        console.log('🏁 useMediaUpload.uploadMultiple - Upload complete:', {
          totalFiles: files.length,
          successCount: results.filter(r => r.success).length,
          failureCount: results.filter(r => !r.success).length,
          results,
        });
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Upload failed';
        console.error('❌ useMediaUpload.uploadMultiple - Unexpected error:', errorMessage);
        setError(errorMessage);
      } finally {
        setIsUploading(false);
        setProgress(null);
      }

      return results;
    },
    []
  );

  const deleteFile = useCallback(async (key: string): Promise<{ success: boolean; error?: string }> => {
    try {
      // Extract bucket and file path from key
      // Using underscore prefix to indicate unused variables
      const [_bucketId, ..._pathParts] = key.split('/');

      // Use Supabase storage service (you may need to add a delete method)
      // For now, return success as Supabase doesn't expose delete in the current service
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Delete failed';
      return { success: false, error: errorMessage };
    }
  }, []);

  return {
    upload,
    uploadMultiple,
    deleteFile,
    isUploading,
    progress,
    error,
    getMediaTypeForFile,
  };
};

export default useMediaUpload;
